/**
 * Admin Analytics Dashboard Page
 * Comprehensive analytics overview for administrators.
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { 
  BarChart3, 
  Calendar, 
  Download, 
  RefreshCw,
  Filter,
  X,
  PieChart as PieChartIcon,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Search
} from 'lucide-react';
import AnalyticsCard from '../../components/Analytics/AnalyticsCard';
import BehaviorPatternBadge from '../../components/Analytics/BehaviorPatternBadge';
import { logError } from '../../utils/errorLogger';
import { useAutoDismiss } from '../../hooks/useAutoDismiss';
import GuestHistoryModal from '../../components/Analytics/GuestHistoryModal';
import SystemMetricsBar from '../../components/Analytics/SystemMetricsBar';
import Button from '../../components/UI/Button';
import DatePicker from '../../components/UI/DatePicker';
import { useAdminAnalytics, useAnalyticsExport } from '../../hooks/useAnalytics';
import { useAppSelector } from '../../store/hooks';
import { analyticsService, GuestBehaviorPattern, GuestBehaviorAnalytics } from '../../services/analyticsService';
import {
  BarChart,
  PieChart,
  lightTheme,
  darkTheme,
  getChartColors
} from '../../components/Charts';
import CustomLegend from '../../components/Charts/CustomLegend';
import './AdminAnalyticsPage.css';

const AdminAnalyticsPage: React.FC = () => {
  const { theme } = useAppSelector((state) => state.ui);
  const { exporting, error: exportError, exportEvents, exportGuests } = useAnalyticsExport();
  
  const [exportSuccess, setExportSuccess] = useState<string | null>(null);
  
  // Auto-dismiss export success message
  useAutoDismiss(exportSuccess, () => setExportSuccess(null), 5000);
  
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: ''
  });
  const [dateError, setDateError] = useState<string>('');
  
  // Validate dates
  React.useEffect(() => {
    if (filters.start_date && filters.end_date) {
      const startDate = new Date(filters.start_date);
      const endDate = new Date(filters.end_date);
      if (startDate > endDate) {
        setDateError('End date must be after start date');
      } else {
        setDateError('');
      }
    } else {
      setDateError('');
    }
  }, [filters.start_date, filters.end_date]);
  
  // Convert filters to format expected by API (ISO date strings)
  const analyticsFilters = React.useMemo(() => {
    // Don't send filters if there's a date validation error
    if (dateError) {
      return undefined;
    }
    
    const apiFilters: { start_date?: string; end_date?: string } = {};
    if (filters.start_date) {
      // HTML date inputs return YYYY-MM-DD format
      // Create a date at midnight local time, then convert to ISO
      const [year, month, day] = filters.start_date.split('-').map(Number);
      const startDate = new Date(year, month - 1, day, 0, 0, 0, 0);
      if (!isNaN(startDate.getTime())) {
        // Convert to ISO string (will include timezone offset)
        apiFilters.start_date = startDate.toISOString();
      }
    }
    if (filters.end_date) {
      // HTML date inputs return YYYY-MM-DD format
      // Create a date at end of day local time, then convert to ISO
      const [year, month, day] = filters.end_date.split('-').map(Number);
      const endDate = new Date(year, month - 1, day, 23, 59, 59, 999);
      if (!isNaN(endDate.getTime())) {
        // Convert to ISO string (will include timezone offset)
        apiFilters.end_date = endDate.toISOString();
      }
    }
    return Object.keys(apiFilters).length > 0 ? apiFilters : undefined;
  }, [filters.start_date, filters.end_date, dateError]);
  
  const { data, loading, error, refetch } = useAdminAnalytics(analyticsFilters);
  
  // Guest Behavior Analytics State
  const [guestBehaviorData, setGuestBehaviorData] = useState<GuestBehaviorAnalytics | null>(null);
  const [guestBehaviorLoading, setGuestBehaviorLoading] = useState(false);
  const [selectedGuestForHistory, setSelectedGuestForHistory] = useState<string | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showBehaviorHelpModal, setShowBehaviorHelpModal] = useState(false);
  const [patternFilter, setPatternFilter] = useState<string>('all');
  const [selectedEventFilter, setSelectedEventFilter] = useState<string>('');
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  // Store all available events for the dropdown (independent of filters)
  const [allAvailableEvents, setAllAvailableEvents] = useState<Array<{event_id: string; event_title: string; event_date: string; host_name?: string}>>([]);
  // Search query for filtering events in the dropdown
  const [eventSearchQuery, setEventSearchQuery] = useState('');
  // Dropdown state
  const [isEventDropdownOpen, setIsEventDropdownOpen] = useState(false);
  const eventDropdownRef = useRef<HTMLDivElement>(null);
  const eventInputRef = useRef<HTMLInputElement>(null);
  
  const chartTheme = theme === 'dark' ? darkTheme : lightTheme;
  const colors = getChartColors(chartTheme, 10);

  // Fetch all available events for the dropdown (without any filters)
  useEffect(() => {
    const fetchAllEvents = async () => {
      try {
        // Fetch without any filters to get all events for dropdown
        // This ensures the dropdown always shows all available events
        const data = await analyticsService.getGuestBehaviorAnalytics({});
        
        // Extract unique events for dropdown
        if (data?.events) {
          const uniqueEvents = data.events.map(event => ({
            event_id: event.event_id,
            event_title: event.event_title,
            event_date: event.event_date,
            host_name: event.host_name
          }));
          setAllAvailableEvents(uniqueEvents);
        }
      } catch (err) {
        logError('Error fetching all events for dropdown', err);
      }
    };

    fetchAllEvents();
  }, []); // Only fetch once on mount, or when component remounts

  // Fetch guest behavior analytics
  useEffect(() => {
    const fetchGuestBehavior = async () => {
      setGuestBehaviorLoading(true);
      try {
        // Build filters object
        const filters: any = {};
        if (patternFilter !== 'all') {
          filters.pattern_type = patternFilter;
        }
        if (selectedEventFilter) {
          filters.event_id = selectedEventFilter;
        }
        
        // Fetch data with all filters applied
        const data = await analyticsService.getGuestBehaviorAnalytics(filters);
        
        setGuestBehaviorData(data);
      } catch (err) {
        logError('Error fetching guest behavior analytics', err);
      } finally {
        setGuestBehaviorLoading(false);
      }
    };

    fetchGuestBehavior();
  }, [patternFilter, selectedEventFilter]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (eventDropdownRef.current && !eventDropdownRef.current.contains(event.target as Node)) {
        setIsEventDropdownOpen(false);
      }
    };

    if (isEventDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEventDropdownOpen]);

  // Filter events for dropdown based on search query
  const filteredEventsForDropdown = useMemo(() => {
    const query = eventSearchQuery.toLowerCase().trim();
    if (!query) return allAvailableEvents;
    
    return allAvailableEvents.filter(event => {
      const eventTitleMatch = event.event_title.toLowerCase().includes(query);
      const hostNameMatch = event.host_name?.toLowerCase().includes(query);
      return eventTitleMatch || hostNameMatch;
    });
  }, [allAvailableEvents, eventSearchQuery]);

  // Process chart data
  const chartData = useMemo(() => {
    if (!data) return null;

    const { category_performance } = data;

    // Category performance pie chart
    const categoryLabels = category_performance.map(cat => cat.category__name || 'Uncategorized');
    const categoryValues = category_performance.map(cat => cat.event_count);
    const categoryColors = colors.slice(0, category_performance.length);
    const categoryTotal = categoryValues.reduce((sum, val) => sum + val, 0);

    // Prepare category legend items with percentages
    const categoryLegendItems = categoryLabels.map((label, index) => ({
      label,
      color: categoryColors[index],
      value: categoryValues[index],
      percentage: categoryTotal > 0 ? (categoryValues[index] / categoryTotal) * 100 : 0
    }));

    const categoryData = {
      labels: categoryLabels,
      datasets: [{
        label: 'Event Count',
        data: categoryValues,
        backgroundColor: categoryColors,
        borderColor: chartTheme.background,
        borderWidth: 2
      }]
    };

    return {
      categoryData,
      categoryLegendItems
    };
  }, [data, colors, chartTheme]);

  const handleExport = async (type: 'events' | 'guests', format: 'csv' | 'excel') => {
    // Clear previous messages
    setExportSuccess(null);
    
    // Format dates properly for backend (ISO 8601 format)
    const exportRequest: any = {
      format,
      include_details: true
    };
    
    // Only include dates if they are provided and valid
    if (filters.start_date) {
      // Convert date string to ISO format with time
      const startDate = new Date(filters.start_date);
      if (!isNaN(startDate.getTime())) {
        exportRequest.start_date = startDate.toISOString();
      }
    }
    
    if (filters.end_date) {
      // Convert date string to ISO format with time (end of day)
      const endDate = new Date(filters.end_date);
      if (!isNaN(endDate.getTime())) {
        // Set to end of day
        endDate.setHours(23, 59, 59, 999);
        exportRequest.end_date = endDate.toISOString();
      }
    }

    try {
      if (type === 'events') {
        await exportEvents(exportRequest);
        setExportSuccess(`Events analytics exported successfully as ${format.toUpperCase()}`);
      } else {
        await exportGuests(exportRequest);
        setExportSuccess(`Guests analytics exported successfully as ${format.toUpperCase()}`);
      }
      
    } catch (err: any) {
      logError('Export error', err);
      // Error is already handled by the hook and will be displayed via exportError
      // The hook sets the error state, so we don't need to do anything here
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#33FF00] mx-auto mb-4"></div>
            <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
              Loading analytics data...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="text-red-500 mb-4">
              <BarChart3 className="w-12 h-12 mx-auto" />
            </div>
            <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
              {error}
            </p>
            <Button
              onClick={refetch}
              className="mt-4"
              variant="outline"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
              No analytics data available
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { system_overview, recent_activity, high_no_show_events } = data;

  return (
    <div className={`analytics-page-container min-h-screen w-full ${theme === 'dark' ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' : 'bg-gradient-to-br from-gray-50 via-white to-gray-50'}`}>
      <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-3 sm:py-4 lg:py-8 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4">
        <div className="flex-shrink-0">
          <h1 className={`text-lg sm:text-xl lg:text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Analytics Dashboard
          </h1>
          <p className={`text-xs sm:text-sm lg:text-base ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
            Comprehensive system analytics and insights
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Button
            onClick={() => setShowFilters(!showFilters)}
            variant="outline"
            className="flex items-center"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
          </Button>
        </div>
      </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className={`relative overflow-hidden rounded-2xl shadow-2xl border backdrop-blur-sm ${
            theme === 'dark' 
              ? 'bg-black/80 border-gray-800/50' 
              : 'bg-white/90 border-gray-200/50'
          }`}>
            <div className={`absolute inset-0 ${
              theme === 'dark' 
                ? 'bg-gradient-to-br from-black via-gray-900/90 to-black' 
                : 'bg-gradient-to-br from-white via-gray-50/80 to-white'
            }`}></div>
            <div className={`absolute inset-0 opacity-5 ${
              theme === 'dark' 
                ? 'bg-gradient-to-br from-[#33FF00]/10 via-transparent to-[#33FF00]/5' 
                : 'bg-gradient-to-br from-[#33FF00]/5 via-transparent to-[#33FF00]/3'
            }`}></div>
            <div className="relative z-10 p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className={`text-base sm:text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Filters
            </h3>
            <Button
              onClick={() => setShowFilters(false)}
              variant="ghost"
              size="sm"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="analytics-filter-row grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <DatePicker
                label="Start Date"
                value={filters.start_date}
                maxDate={filters.end_date || undefined}
                onChange={(value) => {
                  setFilters({ ...filters, start_date: value });
                }}
                placeholder="mm/dd/yyyy"
                error={dateError && filters.start_date && filters.end_date ? dateError : undefined}
              />
            </div>
            
            <div>
              <DatePicker
                label="End Date"
                value={filters.end_date}
                minDate={filters.start_date || undefined}
                onChange={(value) => {
                  setFilters({ ...filters, end_date: value });
                }}
                placeholder="mm/dd/yyyy"
                error={dateError && filters.start_date && filters.end_date ? dateError : undefined}
              />
            </div>
          </div>
            </div>
          </div>
        )}

        {/* System Overview and Recent Activity Cards — aligned with dashboard style */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto px-4 sm:px-6 mb-6">
          <AnalyticsCard
            title="Total Events"
            value={system_overview.total_events}
            className="h-full"
          />
          
          <AnalyticsCard
            title="Active Events"
            value={system_overview.active_events}
            className="h-full"
          />
          
          <AnalyticsCard
            title="Total Guests"
            value={system_overview.total_guests}
            className="h-full"
          />
          
          <AnalyticsCard
            title="Recent Events (7 days)"
            value={recent_activity.recent_events}
            className="h-full"
          />
          
          <AnalyticsCard
            title="Recent Invitations (7 days)"
            value={recent_activity.recent_invitations}
            className="h-full"
          />
          
          <AnalyticsCard
            title="Recent Responses (7 days)"
            value={recent_activity.recent_responses}
            className="h-full"
          />
        </div>

        {/* System Metrics and Category Performance - Side by Side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Category Performance */}
          <div className={`relative overflow-hidden rounded-2xl shadow-2xl border backdrop-blur-sm ${
            theme === 'dark' 
              ? 'bg-black/80 border-gray-800/50' 
              : 'bg-white/90 border-gray-200/50'
          }`}>
            <div className={`absolute inset-0 ${
              theme === 'dark' 
                ? 'bg-gradient-to-br from-black via-gray-900/90 to-black' 
                : 'bg-gradient-to-br from-white via-gray-50/80 to-white'
            }`}></div>
            <div className={`absolute inset-0 opacity-5 ${
              theme === 'dark' 
                ? 'bg-gradient-to-br from-[#33FF00]/10 via-transparent to-[#33FF00]/5' 
                : 'bg-gradient-to-br from-[#33FF00]/5 via-transparent to-[#33FF00]/3'
            }`}></div>
            <div className="relative z-10 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-base sm:text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} flex items-center`}>
              <PieChartIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Category Performance
            </h3>
            <div className="flex items-center space-x-2">
              <PieChartIcon className="w-4 h-4 text-gray-400" />
              <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Distribution</span>
            </div>
          </div>
          {chartData && (
            <div className="analytics-pie-chart-layout flex flex-col lg:flex-row gap-3 sm:gap-4">
              <div className="analytics-chart-container analytics-chart-wrapper h-48 sm:h-56 lg:h-64 flex-1">
                <PieChart
                  data={chartData.categoryData}
                  theme={chartTheme}
                  height={192}
                  title=""
                  showLegend={false}
                  showPercentage={true}
                />
              </div>
              <div className="analytics-pie-legend flex-shrink-0 w-full lg:w-48">
                <CustomLegend
                  items={chartData.categoryLegendItems}
                  maxVisible={5}
                  theme={theme}
                  showPercentage={true}
                  height={192}
                />
              </div>
            </div>
          )}
            </div>
          </div>

          {/* System Metrics Bar Chart */}
          <div className={`relative overflow-hidden rounded-2xl shadow-2xl border backdrop-blur-sm ${
            theme === 'dark' 
              ? 'bg-black/80 border-gray-800/50' 
              : 'bg-white/90 border-gray-200/50'
          }`}>
            <div className={`absolute inset-0 ${
              theme === 'dark' 
                ? 'bg-gradient-to-br from-black via-gray-900/90 to-black' 
                : 'bg-gradient-to-br from-white via-gray-50/80 to-white'
            }`}></div>
            <div className={`absolute inset-0 opacity-5 ${
              theme === 'dark' 
                ? 'bg-gradient-to-br from-[#33FF00]/10 via-transparent to-[#33FF00]/5' 
                : 'bg-gradient-to-br from-[#33FF00]/5 via-transparent to-[#33FF00]/3'
            }`}></div>
            <div className="relative z-10 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-base sm:text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} flex items-center`}>
              <PieChartIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              System Metrics
            </h3>
            <div className="flex items-center space-x-2">
              <PieChartIcon className="w-4 h-4 text-gray-400" />
              <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Overview</span>
            </div>
          </div>
          <div className="analytics-metrics-bar">
            <SystemMetricsBar
              activeEvents={system_overview.active_events}
              pendingEvents={system_overview.pending_approvals}
              totalGuests={system_overview.total_guests}
              acceptedInvitations={system_overview.accepted_invitations}
            />
          </div>
            </div>
          </div>
        </div>

        {/* High No-Show Events Table */}
        <div className={`relative overflow-hidden rounded-2xl shadow-2xl border backdrop-blur-sm ${
          theme === 'dark' 
            ? 'bg-black/80 border-gray-800/50' 
            : 'bg-white/90 border-gray-200/50'
        }`}>
          <div className={`absolute inset-0 ${
            theme === 'dark' 
              ? 'bg-gradient-to-br from-black via-gray-900/90 to-black' 
              : 'bg-gradient-to-br from-white via-gray-50/80 to-white'
          }`}></div>
          <div className={`absolute inset-0 opacity-5 ${
            theme === 'dark' 
              ? 'bg-gradient-to-br from-[#33FF00]/10 via-transparent to-[#33FF00]/5' 
              : 'bg-gradient-to-br from-[#33FF00]/5 via-transparent to-[#33FF00]/3'
          }`}></div>
          <div className="relative z-10">
            <div className={`px-4 sm:px-6 py-3 sm:py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-base sm:text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                High No-Show Events
              </h3>
              <p className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                Events with low acceptance rates
              </p>
            </div>
            
            <div className="analytics-table-wrapper overflow-x-auto">
          <table className={`min-w-full divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
            <thead className={theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}>
              <tr>
                <th className={`px-2 sm:px-3 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                  Event
                </th>
                <th className={`px-2 sm:px-3 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider hidden sm:table-cell`}>
                  Host
                </th>
                <th className={`px-2 sm:px-3 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider hidden sm:table-cell`}>
                  Date
                </th>
                <th className={`px-2 sm:px-3 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                  Invited
                </th>
                <th className={`px-2 sm:px-3 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                  Accepted
                </th>
                <th className={`px-2 sm:px-3 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                  Rate
                </th>
              </tr>
            </thead>
            <tbody className={`${theme === 'dark' ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'} divide-y`}>
              {high_no_show_events.map((event) => (
                <tr key={event.id}>
                  <td className={`px-2 sm:px-3 lg:px-6 py-2 sm:py-3 sm:py-4 text-xs sm:text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} break-words`}>
                    {event.title}
                  </td>
                  <td className={`px-2 sm:px-3 lg:px-6 py-2 sm:py-3 sm:py-4 text-xs sm:text-sm hidden sm:table-cell ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    {event.host}
                  </td>
                  <td className={`px-2 sm:px-3 lg:px-6 py-2 sm:py-3 sm:py-4 text-xs sm:text-sm hidden sm:table-cell ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    {new Date(event.event_date).toLocaleDateString()}
                  </td>
                  <td className={`px-2 sm:px-3 lg:px-6 py-2 sm:py-3 sm:py-4 text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    {event.total_invited}
                  </td>
                  <td className={`px-2 sm:px-3 lg:px-6 py-2 sm:py-3 sm:py-4 text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    {event.accepted_count}
                  </td>
                  <td className={`px-2 sm:px-3 lg:px-6 py-2 sm:py-3 sm:py-4 text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                      event.acceptance_rate < 30 
                        ? theme === 'dark' ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800'
                        : event.acceptance_rate < 50
                        ? theme === 'dark' ? 'bg-yellow-900 text-yellow-200' : 'bg-yellow-100 text-yellow-800'
                        : theme === 'dark' ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800'
                    }`}>
                      {event.acceptance_rate}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
            </div>
          </div>
        </div>

        {/* Guest Behavior Analytics Section */}
        <div className={`relative overflow-hidden rounded-2xl shadow-2xl border backdrop-blur-sm ${
          theme === 'dark' 
            ? 'bg-black/80 border-gray-800/50' 
            : 'bg-white/90 border-gray-200/50'
        }`}>
          <div className={`absolute inset-0 ${
            theme === 'dark' 
              ? 'bg-gradient-to-br from-black via-gray-900/90 to-black' 
              : 'bg-gradient-to-br from-white via-gray-50/80 to-white'
          }`}></div>
          <div className={`absolute inset-0 opacity-5 ${
            theme === 'dark' 
              ? 'bg-gradient-to-br from-[#33FF00]/10 via-transparent to-[#33FF00]/5' 
              : 'bg-gradient-to-br from-[#33FF00]/5 via-transparent to-[#33FF00]/3'
          }`}></div>
          <div className="relative z-10 p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6">
              <div>
                <h2 className={`text-lg sm:text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Guest Behavior Analysis
                </h2>
                <p className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                  Analyze guest engagement patterns and behavior history
                </p>
              </div>
              <button
                onClick={() => setShowBehaviorHelpModal(true)}
                className={`flex items-center justify-center gap-2 px-3 py-2 text-xs sm:text-sm font-medium border rounded-lg transition-colors ${
                  theme === 'dark' 
                    ? 'text-blue-400 bg-blue-900/20 hover:bg-blue-900/30 border-blue-700' 
                    : 'text-blue-600 bg-blue-50 hover:bg-blue-100 border-blue-200'
                }`}
                title="Learn more about behavior patterns"
              >
                <HelpCircle className="w-4 h-4" />
                <span className="hidden sm:inline">Need Help?</span>
                <span className="sm:hidden">Help</span>
              </button>
            </div>

            {/* Filters */}
            <div className="mb-4 sm:mb-6 space-y-3 sm:space-y-4">
              {/* First Row: Event Filter, Pattern Filter, and Search */}
              <div className="analytics-filter-row flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
                <div className="flex-1 w-full sm:min-w-[200px] lg:min-w-[250px]">
                  <label className={`block text-xs sm:text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1.5 sm:mb-2`}>
                    Filter by Event
                  </label>
                  <div className="flex gap-2">
                    <div className="flex-1 relative" ref={eventDropdownRef}>
                      {/* Selected Event Display / Search Input */}
                      <div
                        className={`relative w-full border rounded-lg transition-all duration-200 ${
                          isEventDropdownOpen
                            ? 'ring-2 ring-blue-500 border-blue-500'
                            : theme === 'dark'
                            ? 'border-gray-600 hover:border-gray-500'
                            : 'border-gray-300 hover:border-gray-400'
                        } ${theme === 'dark' ? 'bg-gray-700' : 'bg-white'}`}
                        onClick={() => {
                          setIsEventDropdownOpen(true);
                          setTimeout(() => eventInputRef.current?.focus(), 0);
                        }}
                      >
                        <div className="flex items-center">
                          <Search className={`absolute left-3 h-4 w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                          <input
                            ref={eventInputRef}
                            type="text"
                            placeholder={selectedEventFilter 
                              ? allAvailableEvents.find(e => e.event_id === selectedEventFilter)?.event_title || "Search by event name or host..."
                              : "Search by event name or host..."
                            }
                            value={selectedEventFilter && !isEventDropdownOpen
                              ? allAvailableEvents.find(e => e.event_id === selectedEventFilter)?.event_title || ''
                              : eventSearchQuery
                            }
                            onChange={(e) => {
                              setEventSearchQuery(e.target.value);
                              setIsEventDropdownOpen(true);
                              // Clear selection when user starts typing
                              if (selectedEventFilter) {
                                setSelectedEventFilter('');
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && filteredEventsForDropdown.length > 0) {
                                e.preventDefault();
                                const firstMatch = filteredEventsForDropdown[0];
                                setSelectedEventFilter(firstMatch.event_id);
                                const newExpanded = new Set(expandedEvents);
                                newExpanded.add(firstMatch.event_id);
                                setExpandedEvents(newExpanded);
                                setEventSearchQuery('');
                                setIsEventDropdownOpen(false);
                              } else if (e.key === 'Escape') {
                                setIsEventDropdownOpen(false);
                                setEventSearchQuery('');
                              }
                            }}
                            onFocus={() => setIsEventDropdownOpen(true)}
                            className={`w-full pl-10 pr-10 py-2.5 text-xs sm:text-sm rounded-lg focus:outline-none ${
                              theme === 'dark' 
                                ? 'bg-gray-700 text-white placeholder-gray-400' 
                                : 'bg-white text-gray-900 placeholder-gray-500'
                            }`}
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (selectedEventFilter) {
                                setSelectedEventFilter('');
                                setEventSearchQuery('');
                              } else {
                                setIsEventDropdownOpen(!isEventDropdownOpen);
                              }
                            }}
                            className={`absolute right-2 p-1.5 rounded-md transition-colors ${
                              theme === 'dark'
                                ? 'hover:bg-gray-600 text-gray-400'
                                : 'hover:bg-gray-100 text-gray-500'
                            }`}
                          >
                            {selectedEventFilter ? (
                              <X className="h-4 w-4" />
                            ) : (
                              <ChevronDown className={`h-4 w-4 transition-transform ${isEventDropdownOpen ? 'rotate-180' : ''}`} />
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Dropdown List */}
                      {isEventDropdownOpen && (
                        <div className={`absolute z-50 w-full mt-1 rounded-lg shadow-xl border max-h-64 overflow-y-auto ${
                          theme === 'dark'
                            ? 'bg-gray-800 border-gray-700'
                            : 'bg-white border-gray-200'
                        }`}>
                          {filteredEventsForDropdown.length > 0 ? (
                            <div className="py-1">
                              {filteredEventsForDropdown.map((event) => {
                                const isSelected = selectedEventFilter === event.event_id;
                                return (
                                  <div
                                    key={event.event_id}
                                    onClick={() => {
                                      setSelectedEventFilter(event.event_id);
                                      const newExpanded = new Set(expandedEvents);
                                      newExpanded.add(event.event_id);
                                      setExpandedEvents(newExpanded);
                                      setEventSearchQuery('');
                                      setIsEventDropdownOpen(false);
                                    }}
                                    className={`px-4 py-3 cursor-pointer transition-colors border-b last:border-b-0 ${
                                      isSelected
                                        ? theme === 'dark'
                                          ? 'bg-blue-900/30 border-blue-700/50'
                                          : 'bg-blue-50 border-blue-200'
                                        : theme === 'dark'
                                        ? 'hover:bg-gray-700 border-gray-700'
                                        : 'hover:bg-gray-50 border-gray-100'
                                    }`}
                                  >
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="flex-1 min-w-0">
                                        <div className={`font-medium text-sm mb-1 ${
                                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                                        }`}>
                                          {event.event_title}
                                          {isSelected && (
                                            <span className="ml-2 text-blue-500">✓</span>
                                          )}
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2 text-xs">
                                          <span className={`flex items-center gap-1 ${
                                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                          }`}>
                                            <Calendar className="h-3 w-3" />
                                            {new Date(event.event_date).toLocaleDateString()}
                                          </span>
                                          {event.host_name && (
                                            <span className={`${
                                              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                            }`}>
                                              • {event.host_name}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className={`px-4 py-8 text-center ${
                              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              <p className="text-sm">No events found</p>
                              <p className="text-xs mt-1">Try a different search term</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="w-full sm:min-w-[180px] lg:min-w-[200px] sm:flex-shrink-0">
                  <label className={`block text-xs sm:text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1.5 sm:mb-2`}>
                    Pattern Type
                  </label>
                  <select
                    value={patternFilter}
                    onChange={(e) => setPatternFilter(e.target.value)}
                    className={`w-full px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 text-xs sm:text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      theme === 'dark' 
                        ? 'bg-gray-700 text-white border-gray-600' 
                        : 'bg-white text-gray-900 border-gray-300'
                    }`}
                  >
                    <option value="all">All Patterns</option>
                    <option value="highly_responsive">Highly Responsive</option>
                    <option value="slow_responder">Slow Responder</option>
                    <option value="non_responsive">Non-Responsive</option>
                    <option value="reliable_attender">Reliable Attender</option>
                    <option value="frequent_no_show">Frequent No-Show</option>
                    <option value="consistent_decliner">Consistent Decliner</option>
                    <option value="inconsistent">Inconsistent</option>
                    <option value="insufficient_data">Insufficient Data</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Overview Metrics — aligned with dashboard style */}
            {guestBehaviorData && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 max-w-6xl mx-auto px-4 sm:px-6 mb-6">
                <AnalyticsCard
              title="Total Analyzed"
              value={guestBehaviorData.total_guests_analyzed.toLocaleString()}
              subtitle={`${guestBehaviorData.total_invitations_sent.toLocaleString()} invitations sent`}
            />
            <AnalyticsCard
              title="Avg Response Rate"
              value={`${guestBehaviorData.average_response_rate.toFixed(1)}%`}
              subtitle="Across all guests"
            />
            <AnalyticsCard
              title="Avg No-Show Rate"
              value={`${guestBehaviorData.average_no_show_rate.toFixed(1)}%`}
              subtitle="Accepted but didn't attend"
            />
            <AnalyticsCard
              title="Behavior Patterns"
              value={Object.keys(guestBehaviorData.pattern_distribution).length}
              subtitle="Different patterns identified"
            />
              </div>
            )}

                {/* Pattern Distribution Chart */}
            {guestBehaviorData && (() => {
              // Define all possible patterns
              const allPatterns = [
                'highly_responsive',
                'reliable_attender',
                'slow_responder',
                'non_responsive',
                'frequent_no_show',
                'consistent_decliner',
                'inconsistent',
                'insufficient_data'
              ];

              // Create data for all patterns, defaulting to 0 if not present
              const chartLabels = allPatterns.map(label => 
                label.split('_').map(word => 
                  word.charAt(0).toUpperCase() + word.slice(1)
                ).join(' ')
              );
              const chartData = allPatterns.map(pattern => 
                guestBehaviorData.pattern_distribution[pattern] || 0
              );

              return (
                <div className="mb-4 sm:mb-6">
                  <h3 className={`text-base sm:text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-3 sm:mb-4`}>
                    Overall Pattern Distribution
                  </h3>
                  <div className={`analytics-pattern-chart ${theme === 'dark' ? 'bg-gray-700/50 border-gray-600/50' : 'bg-white/50 border-gray-200/50'} rounded-xl p-3 sm:p-4 lg:p-6 border backdrop-blur-sm`}>
                <div className="analytics-chart-container analytics-chart-wrapper">
                  <BarChart
                    data={{
                      labels: chartLabels,
                      datasets: [{
                        label: 'Number of Guests',
                        data: chartData,
                        backgroundColor: `${colors[0]}40`,
                        borderColor: colors[0],
                        borderWidth: 2
                      }]
                    }}
                    height={250}
                    theme={chartTheme}
                    />
                </div>
                  </div>
                </div>
              );
            })()}

                {/* Event-Grouped Guest Behavior */}
            {guestBehaviorLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className={`ml-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Loading guest behavior data...</span>
              </div>
            ) : guestBehaviorData && guestBehaviorData.events ? (
              <div className="space-y-4">
                {guestBehaviorData.events.filter((eventGroup) => {
              // Filter by selected event
              if (selectedEventFilter && eventGroup.event_id !== selectedEventFilter) {
                return false;
              }
              return true;
                }).map((eventGroup) => {
              // Filter guests by pattern type
              const filteredGuests = eventGroup.guests.filter((guest) => {
                // Filter by pattern type
                if (patternFilter !== 'all' && guest.pattern_type !== patternFilter) {
                  return false;
                }
                return true;
              });

              // Skip events with no matching guests after filtering
              if (filteredGuests.length === 0) {
                return null;
              }

                const isExpanded = expandedEvents.has(eventGroup.event_id);
                const eventDate = new Date(eventGroup.event_date);

                return (
                  <div
                    key={eventGroup.event_id}
                    className={`border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} rounded-lg overflow-hidden`}
                  >
                    {/* Event Header - Clickable */}
                    <button
                      onClick={() => {
                        const newExpanded = new Set(expandedEvents);
                        if (isExpanded) {
                          newExpanded.delete(eventGroup.event_id);
                        } else {
                          newExpanded.add(eventGroup.event_id);
                        }
                        setExpandedEvents(newExpanded);
                      }}
                      className={`w-full px-4 sm:px-6 py-3 sm:py-4 transition-colors flex items-center justify-between text-left ${
                        theme === 'dark' 
                          ? 'bg-gray-700 hover:bg-gray-600' 
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                          <h3 className={`analytics-event-header-title text-sm sm:text-base lg:text-lg font-semibold break-words ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            {eventGroup.event_title}
                          </h3>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                            theme === 'dark' 
                              ? 'bg-blue-900 text-blue-200' 
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {filteredGuests.length} {filteredGuests.length === 1 ? 'guest' : 'guests'}
                          </span>
                        </div>
                        <div className={`mt-1 flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                            {eventDate.toLocaleDateString()}
                            {eventGroup.event_time && (
                              <span className="ml-1">
                                {new Date(`2000-01-01T${eventGroup.event_time}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                          </span>
                          <span className="truncate">{eventGroup.location}</span>
                        </div>
                      </div>
                      <div className="ml-2 sm:ml-4 flex-shrink-0">
                        {isExpanded ? (
                          <ChevronUp className={`w-4 h-4 sm:w-5 sm:h-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                        ) : (
                          <ChevronDown className={`w-4 h-4 sm:w-5 sm:h-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                        )}
                      </div>
                    </button>

                    {/* Event Guests Table - Collapsible */}
                    {isExpanded && (
                      <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-t`}>
                        <div className="analytics-guest-table-wrapper analytics-table-wrapper overflow-x-auto">
                          <table className={`min-w-full divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
                            <thead className={theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}>
                              <tr>
                                <th className={`px-2 sm:px-3 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                                  Guest
                                </th>
                                <th className={`px-2 sm:px-3 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                                  Pattern
                                </th>
                                <th className={`px-2 sm:px-3 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                                  Invitations
                                </th>
                                <th className={`px-2 sm:px-3 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider hidden md:table-cell`}>
                                  Response Rate
                                </th>
                                <th className={`px-2 sm:px-3 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider hidden md:table-cell`}>
                                  No-Show Rate
                                </th>
                                <th className={`px-2 sm:px-3 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody className={`${theme === 'dark' ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'} divide-y`}>
                              {filteredGuests.map((guest) => (
                                <tr key={guest.guest}>
                                  <td className="analytics-guest-cell px-2 sm:px-3 lg:px-6 py-2 sm:py-3 sm:py-4">
                                    <div>
                                      <div className={`text-xs sm:text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} break-words`}>
                                        {guest.guest_name}
                                      </div>
                                      <div className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {guest.guest_phone}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-2 sm:px-3 lg:px-6 py-2 sm:py-3 sm:py-4">
                                    <BehaviorPatternBadge patternType={guest.pattern_type} />
                                  </td>
                                  <td className={`px-2 sm:px-3 lg:px-6 py-2 sm:py-3 sm:py-4 text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {guest.total_invitations}
                                  </td>
                                  <td className="px-2 sm:px-3 lg:px-6 py-2 sm:py-3 sm:py-4 hidden md:table-cell">
                                    <div className="flex items-center">
                                      <span className={`text-xs sm:text-sm mr-1 sm:mr-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                        {guest.response_rate.toFixed(1)}%
                                      </span>
                                      <div className={`flex-1 rounded-full h-1.5 sm:h-2 max-w-[80px] sm:max-w-[100px] ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`}>
                                        <div
                                          className="bg-blue-600 h-1.5 sm:h-2 rounded-full"
                                          style={{ width: `${guest.response_rate}%` }}
                                        ></div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className={`px-2 sm:px-3 lg:px-6 py-2 sm:py-3 sm:py-4 hidden md:table-cell`}>
                                    <span className={`text-xs sm:text-sm font-medium ${
                                      guest.no_show_rate > 20
                                        ? theme === 'dark' ? 'text-red-400' : 'text-red-600'
                                        : guest.no_show_rate > 10
                                        ? theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
                                        : theme === 'dark' ? 'text-green-400' : 'text-green-600'
                                    }`}>
                                      {guest.no_show_rate.toFixed(1)}%
                                    </span>
                                  </td>
                                  <td className="px-2 sm:px-3 lg:px-6 py-2 sm:py-3 sm:py-4 text-xs sm:text-sm">
                                    <button
                                      onClick={() => {
                                        setSelectedGuestForHistory(guest.guest);
                                        setShowHistoryModal(true);
                                      }}
                                      className={`${theme === 'dark' 
                                        ? 'text-blue-400 hover:text-blue-300' 
                                        : 'text-blue-600 hover:text-blue-800'} text-xs sm:text-sm whitespace-nowrap`
                                      }
                                    >
                                      View History
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
                })}
            
                {/* Show message if no events match filters */}
                {(() => {
                  const filteredEvents = guestBehaviorData.events.filter((eventGroup) => {
                // Filter by selected event
                if (selectedEventFilter && eventGroup.event_id !== selectedEventFilter) {
                  return false;
                }
                return true;
              });

              const hasMatchingEvents = filteredEvents.some((eventGroup) => {
                const filteredGuests = eventGroup.guests.filter((guest) => {
                  if (patternFilter !== 'all' && guest.pattern_type !== patternFilter) {
                    return false;
                  }
                  return true;
                });
                
                return filteredGuests.length > 0;
              });

                  return !hasMatchingEvents && filteredEvents.length === 0 ? (
                    <div className={`text-center py-12 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      No events or guests match the current filters.
                    </div>
                  ) : null;
                })()}
              </div>
            ) : null}
          </div>
        </div>

        {/* Export Section */}
        <div className={`relative overflow-hidden rounded-2xl shadow-2xl border backdrop-blur-sm ${
          theme === 'dark' 
            ? 'bg-black/80 border-gray-800/50' 
            : 'bg-white/90 border-gray-200/50'
        }`}>
          <div className={`absolute inset-0 ${
            theme === 'dark' 
              ? 'bg-gradient-to-br from-black via-gray-900/90 to-black' 
              : 'bg-gradient-to-br from-white via-gray-50/80 to-white'
          }`}></div>
          <div className={`absolute inset-0 opacity-5 ${
            theme === 'dark' 
              ? 'bg-gradient-to-br from-[#33FF00]/10 via-transparent to-[#33FF00]/5' 
              : 'bg-gradient-to-br from-[#33FF00]/5 via-transparent to-[#33FF00]/3'
          }`}></div>
          <div className="relative z-10 p-4 sm:p-6">
            <h3 className={`text-base sm:text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-3 sm:mb-4`}>
              Export Data
            </h3>
            
            {/* Export Status Messages */}
            {exportError && (
              <div className={`mb-3 sm:mb-4 p-2 sm:p-3 border rounded-lg ${
                theme === 'dark' 
                  ? 'bg-red-900/20 border-red-700' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <p className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-red-200' : 'text-red-800'}`}>
                  Export failed: {exportError}
                </p>
              </div>
            )}
            {exportSuccess && (
              <div className={`mb-3 sm:mb-4 p-2 sm:p-3 border rounded-lg ${
                theme === 'dark' 
                  ? 'bg-green-900/20 border-green-700' 
                  : 'bg-green-50 border-green-200'
              }`}>
                <p className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-green-200' : 'text-green-800'}`}>
                  {exportSuccess}
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 sm:space-y-3">
                <h4 className={`text-xs sm:text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Export Events Analytics
                </h4>
                <div className="analytics-export-buttons flex flex-col sm:flex-row gap-2">
                  <Button
                    onClick={() => handleExport('events', 'csv')}
                    disabled={exporting}
                    variant="outline"
                    size="sm"
                    className="flex items-center justify-center w-full sm:w-auto"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    CSV
                  </Button>
                  <Button
                    onClick={() => handleExport('events', 'excel')}
                    disabled={exporting}
                    variant="outline"
                    size="sm"
                    className="flex items-center justify-center w-full sm:w-auto"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Excel
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2 sm:space-y-3">
                <h4 className={`text-xs sm:text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Export Guests Analytics
                </h4>
                <div className="analytics-export-buttons flex flex-col sm:flex-row gap-2">
                  <Button
                    onClick={() => handleExport('guests', 'csv')}
                    disabled={exporting}
                    variant="outline"
                    size="sm"
                    className="flex items-center justify-center w-full sm:w-auto"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    CSV
                  </Button>
                  <Button
                    onClick={() => handleExport('guests', 'excel')}
                    disabled={exporting}
                    variant="outline"
                    size="sm"
                    className="flex items-center justify-center w-full sm:w-auto"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Excel
                  </Button>
                </div>
              </div>
            </div>
        </div>
      </div>

      {/* History Modal */}
      {selectedGuestForHistory && showHistoryModal && (
        <GuestHistoryModal
          guestId={selectedGuestForHistory}
          guestName={(() => {
            if (!guestBehaviorData?.events) return 'Guest';
            for (const eventGroup of guestBehaviorData.events) {
              const guest = eventGroup.guests.find((g: GuestBehaviorPattern) => g.guest === selectedGuestForHistory);
              if (guest) return guest.guest_name;
            }
            return 'Guest';
          })()}
          isOpen={showHistoryModal}
          onClose={() => {
            setShowHistoryModal(false);
            setSelectedGuestForHistory(null);
          }}
        />
      )}

      {/* Behavior Help Modal */}
      {showBehaviorHelpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
          {/* Overlay */}
          <div 
            className="fixed inset-0 transition-opacity" 
            onClick={() => setShowBehaviorHelpModal(false)}
          >
            <div className={`absolute inset-0 ${theme === 'dark' ? 'bg-gray-800 opacity-90' : 'bg-gray-900 opacity-75'}`}></div>
          </div>

          {/* Modal */}
          <div className={`analytics-modal-content relative ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-w-[95vw] sm:max-w-3xl sm:max-h-[25vh] md:max-h-[65vh] lg:max-h-[90vh] flex flex-col`}>
              {/* Header */}
              <div className={`${theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} px-4 sm:px-6 py-3 sm:py-4 mt-10 flex items-center justify-between border-b flex-shrink-0`}>
                <div className="flex-1 min-w-0 pr-2">
                  <h3 className={`text-base sm:text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Guest Behavior Patterns - Guide
                  </h3>
                  <p className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                    Understanding behavior categorization and metrics
                  </p>
                </div>
                <button
                  onClick={() => setShowBehaviorHelpModal(false)}
                  className={`transition-colors flex-shrink-0 ${
                    theme === 'dark' 
                      ? 'text-gray-500 hover:text-gray-300' 
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="px-4 sm:px-6 py-4 overflow-y-auto flex-1 min-h-0">
                <div className="space-y-6">
                  {/* Behavior Patterns */}
                  <div>
                    <h4 className={`text-md font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-3`}>
                      Behavior Patterns
                    </h4>
                    <div className="space-y-3">
                      <div className={`${theme === 'dark' ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'} border rounded-lg p-3`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`font-semibold ${theme === 'dark' ? 'text-blue-200' : 'text-blue-900'}`}>Highly Responsive</span>
                        </div>
                        <p className={`text-sm ${theme === 'dark' ? 'text-blue-300' : 'text-blue-800'}`}>
                          Responds quickly (&lt;24 hours) to most invitations (90%+). Great for priority events.
                        </p>
                      </div>

                      <div className={`${theme === 'dark' ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-200'} border rounded-lg p-3`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`font-semibold ${theme === 'dark' ? 'text-yellow-200' : 'text-yellow-900'}`}>Slow Responder</span>
                        </div>
                        <p className={`text-sm ${theme === 'dark' ? 'text-yellow-300' : 'text-yellow-800'}`}>
                          Responds eventually (60%+ response rate) but takes longer (&gt;48 hours). May need reminders.
                        </p>
                      </div>

                      <div className={`${theme === 'dark' ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'} border rounded-lg p-3`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`font-semibold ${theme === 'dark' ? 'text-red-200' : 'text-red-900'}`}>Non-Responsive</span>
                        </div>
                        <p className={`text-sm ${theme === 'dark' ? 'text-red-300' : 'text-red-800'}`}>
                          Rarely responds to invitations (30% or less). Consider alternative communication channels.
                        </p>
                      </div>

                      <div className={`${theme === 'dark' ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200'} border rounded-lg p-3`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`font-semibold ${theme === 'dark' ? 'text-green-200' : 'text-green-900'}`}>Reliable Attender</span>
                        </div>
                        <p className={`text-sm ${theme === 'dark' ? 'text-green-300' : 'text-green-800'}`}>
                          Consistently accepts (80%+) and shows up with low no-show rate (&lt;10%). High-value guests.
                        </p>
                      </div>

                      <div className={`${theme === 'dark' ? 'bg-orange-900/20 border-orange-700' : 'bg-orange-50 border-orange-200'} border rounded-lg p-3`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`font-semibold ${theme === 'dark' ? 'text-orange-200' : 'text-orange-900'}`}>Frequent No-Show</span>
                        </div>
                        <p className={`text-sm ${theme === 'dark' ? 'text-orange-300' : 'text-orange-800'}`}>
                          Accepts invitations but often doesn't attend (50%+ no-show rate). Monitor carefully.
                        </p>
                      </div>

                      <div className={`${theme === 'dark' ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-50 border-purple-200'} border rounded-lg p-3`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`font-semibold ${theme === 'dark' ? 'text-purple-200' : 'text-purple-900'}`}>Consistent Decliner</span>
                        </div>
                        <p className={`text-sm ${theme === 'dark' ? 'text-purple-300' : 'text-purple-800'}`}>
                          Frequently declines invitations but responds quickly. May prefer different event types.
                        </p>
                      </div>

                      <div className={`${theme === 'dark' ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'} border rounded-lg p-3`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`font-semibold ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>Inconsistent</span>
                        </div>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-800'}`}>
                          Behavior varies significantly with no clear pattern. Monitor for emerging trends.
                        </p>
                      </div>

                      <div className={`${theme === 'dark' ? 'bg-gray-900/30 border-gray-600' : 'bg-gray-100 border-gray-300'} border rounded-lg p-3`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Insufficient Data</span>
                        </div>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-700'}`}>
                          Not enough invitation history (less than 2-3 events) to determine a reliable behavior pattern.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Metrics Explanation */}
                  <div>
                    <h4 className={`text-md font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-3`}>
                      Metrics Explained
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Total Invitations:</span>
                        <span className={`font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}>Number of events this guest was invited to</span>
                      </div>
                      <div className="flex justify-between">
                        <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Response Rate:</span>
                        <span className={`font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}>% of invitations that received a response</span>
                      </div>
                      <div className="flex justify-between">
                        <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>No-Show Rate:</span>
                        <span className={`font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}>% of accepted events where guest didn't attend</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className={`${theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} px-4 sm:px-6 py-3 sm:py-4 flex justify-end border-t flex-shrink-0`}>
                <button
                  onClick={() => setShowBehaviorHelpModal(false)}
                  className={`px-4 py-2 text-sm sm:text-base text-white rounded-md transition-colors ${
                    theme === 'dark' 
                      ? 'bg-blue-700 hover:bg-blue-600' 
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  Got it
                </button>
              </div>
            </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default AdminAnalyticsPage;
